import { roleAtLeast, type UserRole } from '@bartizo/shared';
import type { ReactNode } from 'react';
import { PageHeader } from '../components/PageHeader.js';
import { Alert } from '../ui/Alert.js';
import { useSession } from './session-context.js';

/**
 * Hides a route from a role that may not use it (knowledge doc section 17).
 *
 * A courtesy, not a control. Every route behind this is checked again by the API's
 * `requireRole`, because anyone can type a URL and nothing in a browser is trusted.
 * What this buys is a person not being shown a page that will only refuse them.
 */

export function RequireRole({
  minimumRole,
  children,
}: {
  minimumRole: UserRole;
  children: ReactNode;
}): ReactNode {
  const { session } = useSession();
  if (session === null || !roleAtLeast(session.user.role, minimumRole)) {
    return (
      <div className="page">
        <PageHeader title="Not available to your role" />
        <Alert tone="danger">
          This page needs the <strong>{minimumRole}</strong> role. Ask an administrator if you need
          access.
        </Alert>
      </div>
    );
  }
  return children;
}
